import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type RefObject } from "react";
import type { PortraitClips } from "odyssey-shared";
import { REDUCED_MOTION_ATTRIBUTE, REDUCED_MOTION_QUERY } from "../../game/reduced-motion.ts";
import { ArtIcon, type IconArt } from "./ArtIcon.tsx";
import { Glyph, type GlyphName } from "./Glyph.tsx";
import { portraitSeed, runPortrait, type PortraitEnvironment } from "./portrait-player.ts";
import { Tooltip } from "./Tooltip.tsx";
import { Button } from "./Button.tsx";
import { TextField } from "./TextField.tsx";

/**
 * The dialogue window (ADR-136 decision 5.6; the design's "NPC dialogue and story choices", `QuestSurfaces.jsx`): a HUD
 * frame with the design's padding 4, a portrait slot 40 by 48 beside the text column 6 apart (the speaker's look's
 * first idle frame, else the design's grey placeholder), the speaker's name in the pixel face in brass, the line in the
 * body face, and the choices as numbered rows, each a button: the number in the pixel face, muted, the text, an 8 px
 * its marks after it (the author, 2026-10-28: "put them at the end of the option, not the start"; a plain choice has
 * none), each with a tooltip on hover and focus (what it is, the stat it reads, the chance), and a right-hand weight
 * column the wire does not fill yet. Leave is the last numbered row, as in the design, with its mark after the word.
 * It is non-modal: the area around it takes no pointer events, so the world, the bar and the chat stay usable beside
 * it. The page places it (`at`); its width is the compact one the author asked for (2026-10-28), not
 * the design's 520.
 *
 * @example
 * <DialoguePanel speaker="Aethling" text="Hoo." choices={["Who are you?"]} words={{ label: "Conversation with Aethling", leave: "Leave" }} onChoose={send} onLeave={leave} />
 */

/**
 * The window's own words: the name a screen reader gives the dialog, and the Leave row's label.
 */
export interface DialogueWords {
  readonly label: string;
  readonly leave: string;
}

/**
 * A speaker's portrait: a strip of cells and the cell's size in pixels. The slot shows the strip's first cell, or, when
 * the speaker has a clip table (`clips`, P2.22-T37), its rest cell, stepping through the table's clips on their timers
 * and holding the rest cell under reduced motion.
 */
export interface DialoguePortrait {
  readonly url: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly clips?: PortraitClips;
}

/**
 * What the window shows and does.
 */
export interface DialoguePanelProps {
  /** The speaker's name, empty for none. */
  readonly speaker: string;
  /** The line spoken. */
  readonly text: string;
  /** The choices' texts in the order shown. */
  readonly choices: readonly string[];
  /** The window's own words: its label and Leave's. */
  readonly words: DialogueWords;
  /** Chooses a choice by its index, from 0. */
  readonly onChoose: (index: number) => void;
  /** Leaves the conversation. */
  readonly onLeave: () => void;
  /** The speaker's portrait, or null for the placeholder. */
  readonly portrait?: DialoguePortrait | null;
  /** The window's top-left corner in CSS pixels; hidden until the page has placed it. */
  readonly at?: { readonly left: number; readonly top: number } | null;
  /** The marks after each choice, by the choice's index; a choice without an entry has none. */
  readonly marks?: readonly (readonly DialogueMarkView[])[];
  /** The Leave row's mark. */
  readonly leaveMark?: DialogueMarkView;
  /** The dialog's element, for the page's focus handling. */
  readonly panel?: RefObject<HTMLDivElement | null>;
  /** The companion's naming, shown in place of the choices while the server asks for a name; none otherwise. */
  readonly naming?: DialogueNaming | null;
  /** The speaker's mood tier: its word beside the name, and its tooltip; null for none. */
  readonly tier?: DialogueTierView | null;
}

/**
 * A speaker's mood tier as the window draws it (ADR-146 decision 8.2): the tier's word beside the name and, in the
 * tooltip the word opens on hover, focus or a tap, a thin bar with a pip for each tier up to the current one, the next
 * tier's word and, for a speaker that can be fed, whether it still wants today's feed. `lore` is the second section
 * only a beast master is given; it is absent for everyone else and then not drawn. The standing itself never reaches
 * the page.
 */
export interface DialogueTierView {
  readonly name: string;
  /** The current tier's place among the tiers, from 0. */
  readonly at: number;
  /** How many tiers there are. */
  readonly of: number;
  /** The pips' accessible name. */
  readonly label: string;
  /** The next tier named in words, or null at the last tier. */
  readonly next: string | null;
  /** Whether today's feed is wanted, in words; null for none. */
  readonly meal: string | null;
  /** What a beast master is told in addition, or null for none. */
  readonly lore?: DialogueLoreView | null;
}

/**
 * The tooltip's second section for a holder of Animal Lore: its heading, the exact bar toward the next tier (a fraction
 * from 0 to 1) and its notes.
 */
export interface DialogueLoreView {
  readonly title: string;
  readonly fill: number;
  readonly lines: readonly string[];
}

/**
 * The companion's naming at the offer (ADR-142 decision 5.4, P2.26-T7), shown in place of the choices: a name field and
 * the button that sends it, with why the last name was refused under the field. While a name is on its way the field
 * and the button wait.
 */
export interface DialogueNaming {
  readonly words: { readonly label: string; readonly hint: string; readonly submit: string };
  /** The pattern a name must match, as an `<input>`'s `pattern` reads it. */
  readonly pattern: string;
  readonly refused: string | null;
  readonly pending: boolean;
  readonly onSubmit: (name: string) => void;
}

/**
 * A mark at the end of a choice (an attribute check, a background, a locked choice, a weight, Leave): its colour art,
 * or null where the page has none, the glyph drawn instead, and the tooltip's lines, the first its name.
 */
export interface DialogueMarkView {
  readonly art: IconArt | null;
  readonly glyph: GlyphName;
  readonly tip: readonly string[];
}

/**
 * The room a mark takes in the row, in logical pixels: the row's line height. Its art is the 16 box, whose 32 by 32
 * cell holds a 24 by 24 glyph centred, so the drawn glyph is 12 logical at a UI scale of 2.
 */
const MARK_ROOM = 12;

/**
 * The portrait slot's size in logical pixels, the design's.
 */
const SLOT = { width: 40, height: 48 } as const;

/**
 * The page's timers and reduced motion for a portrait: the root's `data-reduced-motion` when the page has set it, else
 * the device's preference, heard again whenever either changes.
 */
const PAGE_PORTRAIT: PortraitEnvironment = {
  clock: { setTimeout: (run, ms) => window.setTimeout(run, ms), clearTimeout: (handle) => window.clearTimeout(handle as number) },
  reduced: () => {
    const choice = document.documentElement.getAttribute(REDUCED_MOTION_ATTRIBUTE);
    return choice === "on" || (choice !== "off" && window.matchMedia(REDUCED_MOTION_QUERY).matches);
  },
  watch: (onChange) => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const observer = new MutationObserver(onChange);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: [REDUCED_MOTION_ATTRIBUTE] });
    query.addEventListener("change", onChange);
    return () => {
      observer.disconnect();
      query.removeEventListener("change", onChange);
    };
  },
};

/**
 * How many portraits the page has opened, which seeds each so no two conversations replay one sequence.
 */
let portraitsOpened = 0;

/**
 * The cell of a portrait's strip being shown: the first for a strip with no clips, else the rest cell and each cell of
 * the clips the table plays from the moment the window mounts, following reduced motion as it changes, until it
 * unmounts or the portrait changes.
 *
 * @param portrait The portrait.
 * @returns The cell's index in the strip.
 */
function useFaceCell(portrait: DialoguePortrait): number {
  const clips = portrait.clips;
  const [cell, setCell] = useState(clips?.rest ?? 0);
  const [opened] = useState(() => portraitsOpened++);
  useEffect(() => {
    if (clips === undefined) {
      setCell(0);
      return undefined;
    }
    return runPortrait(clips, portraitSeed(portrait.url, opened), PAGE_PORTRAIT, setCell);
  }, [clips, portrait.url, opened]);
  return cell;
}

/**
 * The portrait slot: the frame scaled to fit the slot and centred in it, or the design's grey block.
 *
 * @param props The `portrait`, or null.
 * @returns The slot.
 */
function Portrait({ portrait }: { readonly portrait: DialoguePortrait | null }): ReactElement {
  return portrait === null ? <div className="ot-dialogue__portrait ot-dialogue__portrait--empty" data-kit="dialogue-portrait" aria-hidden="true" /> : <PortraitFace key={portrait.url} portrait={portrait} />;
}

/**
 * The portrait's face: the cell shown, scaled to fit the slot and centred in it. A cell's offset is `cell * width`
 * logical pixels, exact when the scaled cell width is a whole number (the Aethling's 30 by 36 cells scale by 4/3
 * to the slot's 40 by 48), so a strip of other cell sizes is checked at the slot before it is shipped.
 *
 * @param props The `portrait`.
 * @returns The slot.
 */
function PortraitFace({ portrait }: { readonly portrait: DialoguePortrait }): ReactElement {
  const cell = useFaceCell(portrait);
  const scale = Math.min(SLOT.width / portrait.frameWidth, SLOT.height / portrait.frameHeight);
  const width = portrait.frameWidth * scale;
  const height = portrait.frameHeight * scale;
  return (
    <div className="ot-dialogue__portrait" data-kit="dialogue-portrait" aria-hidden="true">
      <div className="ot-dialogue__face" style={{ width: `calc(${width} * var(--px))`, height: `calc(${height} * var(--px))`, backgroundImage: `url("${portrait.url}")`, backgroundSize: `auto calc(${height} * var(--px))`, backgroundPosition: `calc(${-cell * width} * var(--px)) 0` }} data-cell={cell} />
    </div>
  );
}

/**
 * One mark after a choice's text: its art, else its glyph, and its tooltip above it while `tipped`. A touch has no
 * hover, so a tap on the mark opens and closes its tooltip instead of choosing the row.
 *
 * @param props The `mark`, the tooltip's `id`, whether it is `tipped`, what the pointer entering and leaving it does,
 *   and what a tap does.
 * @returns The mark.
 */
function Mark({ mark, id, tipped, onEnter, onLeave, onTap }: { readonly mark: DialogueMarkView; readonly id: string; readonly tipped: boolean; readonly onEnter: () => void; readonly onLeave: () => void; readonly onTap: () => void }): ReactElement {
  const touched = useRef(false);
  return (
    <span
      className="ot-dialogue__mark"
      data-kit="dialogue-mark"
      data-icon={mark.art?.key}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") {
          onEnter();
        }
      }}
      onPointerLeave={onLeave}
      onPointerDown={(event) => {
        touched.current = event.pointerType !== "mouse";
      }}
      onClick={(event) => {
        if (touched.current) {
          event.stopPropagation();
          event.preventDefault();
          touched.current = false;
          onTap();
        }
      }}
    >
      {mark.art === null ? <Glyph name={mark.glyph} /> : <ArtIcon art={mark.art} room={MARK_ROOM} at={null} />}
      {tipped ? <Tooltip id={id} text={mark.tip[0] ?? ""} lines={mark.tip.length > 1 ? mark.tip : undefined} left={0} bottom={MARK_ROOM + 2} /> : null}
    </span>
  );
}

/**
 * One numbered row. A mark's tooltip shows while the mouse is over the mark, or from a tap on it until the next tap; the
 * first mark's shows while the row holds the keyboard's focus. Every mark's lines are also the row's accessible
 * description, in a visually hidden span, so a screen reader hears each check, lock and weight at rest and not only the
 * first.
 *
 * @param props The row's `number` from 1, its `text`, the `marks` after the text and what a click does.
 * @returns The row.
 */
function Row({ number, marks, text, onClick }: { readonly number: number; readonly marks: readonly DialogueMarkView[]; readonly text: string; readonly onClick: () => void }): ReactElement {
  const base = useId();
  const [hovered, setHovered] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState<number | null>(null);
  const tipped = hovered ?? pinned ?? (focused && marks.length > 0 ? 0 : null);
  return (
    <li>
      <button type="button" className="ot-dialogue__choice" data-kit="dialogue-choice" onClick={onClick} aria-describedby={marks.length === 0 ? undefined : `${base}-said`} onFocus={(event) => setFocused(event.currentTarget.matches(":focus-visible"))} onBlur={() => setFocused(false)}>
        <span className="ot-dialogue__number">{number}</span>
        <span className="ot-dialogue__text">{text}</span>
        {marks.map((mark, index) => (
          <Mark key={index} mark={mark} id={`${base}-${index}`} tipped={tipped === index} onEnter={() => setHovered(index)} onLeave={() => setHovered(null)} onTap={() => setPinned((open) => (open === index ? null : index))} />
        ))}
        <span className="ot-dialogue__weight" data-kit="dialogue-weight" />
        {marks.length === 0 ? null : (
          <span id={`${base}-said`} className="ot-dialogue__said" data-kit="dialogue-said">
            {marks.map((mark) => mark.tip.join(". ")).join(". ")}
          </span>
        )}
      </button>
    </li>
  );
}

/**
 * The name field of the companion's naming: the kit's text field held to the first-name rule's 12 characters, focused
 * as it opens and again whenever a name comes back refused, so the player can type at once, and the button that sends
 * it, centred on the field's box whatever the label and the note under it measure; Enter sends it too.
 *
 * @param props The `naming`.
 * @returns The form.
 */
function NamingForm({ naming }: { readonly naming: DialogueNaming }): ReactElement {
  const [name, setName] = useState("");
  const [lift, setLift] = useState(0);
  const form = useRef<HTMLFormElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const send = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const box = input.current?.closest(".ot-field__box")?.getBoundingClientRect();
    const own = form.current?.getBoundingClientRect();
    const button = send.current?.getBoundingClientRect();
    if (box !== undefined && own !== undefined && button !== undefined) {
      setLift(Math.max(0, Math.round(box.top - own.top + (box.height - button.height) / 2)));
    }
  }, [naming.refused, naming.pending]);

  useEffect(() => {
    if (!naming.pending) {
      input.current?.focus();
    }
  }, [naming.pending, naming.refused]);

  return (
    <form
      ref={form}
      className="ot-dialogue__naming"
      data-kit="dialogue-naming"
      onSubmit={(event) => {
        event.preventDefault();
        naming.onSubmit(name);
      }}
    >
      <TextField label={naming.words.label} value={name} onChange={setName} {...(naming.refused === null ? { hint: naming.words.hint } : { error: naming.refused })} maxLength={12} minLength={3} pattern={naming.pattern} required autoFocus disabled={naming.pending} autoComplete="off" name="companion-name" inputRef={input} />
      <span ref={send} className="ot-dialogue__naming-send" style={{ marginTop: `${lift}px` }}>
        <Button type="submit" variant="primary" disabled={naming.pending || name.trim().length < 3} busy={naming.pending}>
          {naming.words.submit}
        </Button>
      </span>
    </form>
  );
}

/**
 * The mood word in the header and the tooltip it opens: while the mouse is over the word, from a tap on it until the
 * next tap, or while it holds the keyboard's focus. The tooltip holds the thin pip bar, the next tier, today's meal
 * and, for a beast master, a second section with the exact bar and notes. Its lines are also the word's accessible
 * description, in a visually hidden span.
 *
 * @param props The `tier`.
 * @returns The word.
 */
function MoodWord({ tier }: { readonly tier: DialogueTierView }): ReactElement {
  const id = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const touched = useRef(false);
  const open = hovered || focused || pinned;
  const lines = [tier.next, tier.meal].filter((line): line is string => line !== null);
  const lore = tier.lore ?? null;
  return (
    <span className="ot-dialogue__tier-box">
      <button
        type="button"
        className="ot-dialogue__tier"
        data-kit="dialogue-tier"
        aria-describedby={`${id}-said`}
        aria-expanded={open}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") {
            setHovered(true);
          }
        }}
        onPointerLeave={() => setHovered(false)}
        onPointerDown={(event) => {
          touched.current = event.pointerType !== "mouse";
        }}
        onClick={() => {
          if (touched.current) {
            touched.current = false;
            setPinned((was) => !was);
          }
        }}
        onFocus={(event) => setFocused(event.currentTarget.matches(":focus-visible"))}
        onBlur={() => setFocused(false)}
      >
        {tier.name}
      </button>
      <span id={`${id}-said`} className="ot-dialogue__said" data-kit="dialogue-said">
        {[tier.label, ...lines, ...(lore === null ? [] : [lore.title, ...lore.lines])].join(". ")}
      </span>
      {open ? (
        <Tooltip id={id} text={tier.name} lines={[tier.name, ...lines]} right={0} top={14}>
          <span className="ot-dialogue__pips" role="img" aria-label={tier.label} data-kit="dialogue-pips" data-at={tier.at} data-of={tier.of}>
            {Array.from({ length: tier.of }, (_, index) => (
              <i key={index} className={index <= tier.at ? "ot-dialogue__pip ot-dialogue__pip--lit" : "ot-dialogue__pip"} />
            ))}
          </span>
          {lore === null ? null : (
            <span className="ot-dialogue__lore" data-kit="dialogue-lore">
              <span className="ot-dialogue__lore-title">{lore.title}</span>
              <span className="ot-dialogue__bar" data-kit="dialogue-lore-bar">
                <b style={{ width: `${Math.round(Math.min(1, Math.max(0, lore.fill)) * 100)}%` }} />
              </span>
              {lore.lines.map((line, index) => (
                <span key={index} className="ot-tooltip__line">
                  {line}
                </span>
              ))}
            </span>
          )}
        </Tooltip>
      ) : null}
    </span>
  );
}

/**
 * The dialogue window.
 *
 * @param props What it shows and does.
 * @returns The window over a pointer-transparent area.
 */
export function DialoguePanel({ speaker, text, choices, words, onChoose, onLeave, panel, at = null, portrait = null, marks = [], leaveMark = { art: null, glyph: "close-small", tip: [words.leave] }, naming = null, tier = null }: DialoguePanelProps): ReactElement {
  const shown = naming === null ? choices : [];
  return (
    <div className="ot-dialogue-area" data-kit="dialogue-area">
      <div ref={panel} role="dialog" aria-modal="false" aria-label={words.label} className="ot-hud ot-ui-frame-hud ot-dialogue" data-kit="dialogue" style={at === null ? { visibility: "hidden" } : { left: `${at.left}px`, top: `${at.top}px` }}>
        <Portrait portrait={portrait} />
        <div className="ot-dialogue__column">
          {speaker === "" && tier === null ? null : (
            <div className="ot-dialogue__speaker" data-kit="dialogue-speaker">
              {tier === null ? speaker : <span>{speaker}</span>}
              {tier === null ? null : <MoodWord tier={tier} />}
            </div>
          )}
          <div className="ot-dialogue__line" data-kit="dialogue-line" aria-live="polite">
            {text}
          </div>
          {naming === null ? null : <NamingForm naming={naming} />}
          <ol className="ot-dialogue__choices" data-kit="dialogue-choices">
            {shown.map((choice, index) => (
              <Row key={index} number={index + 1} marks={marks[index] ?? []} text={choice} onClick={() => onChoose(index)} />
            ))}
            <Row number={shown.length + 1} marks={[leaveMark]} text={words.leave} onClick={onLeave} />
          </ol>
        </div>
      </div>
    </div>
  );
}
